import React, { useState } from 'react';
import { useTutorials } from '../hooks/useTutorials';
import { setLanguage } from '../i18n';
import { Link } from 'react-router-dom';
import {
  Building2, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube,
  Link as LinkIcon, Wrench, Landmark, Briefcase, ChevronDown, Languages, RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCms } from '../hooks/useCms';
import BetaBadge from './BetaBadge';
import NewsletterSignup from './NewsletterSignup';

// Brand SVG icons (lucide-react doesn't ship TikTok / WhatsApp / Telegram)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.27 8.27 0 0 0 4.83 1.55v-3.4a4.85 4.85 0 0 1-1.9-.39z" />
  </svg>
);

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

const TelegramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

/**
 * FooterGroup — collapsible wrapper for footer link sections.
 *
 * Mobile (< sm): title acts as a button, content collapsed by default.
 * Desktop (≥ sm): button still rendered for visual consistency but
 * pointer-events: none and content always visible (no toggle UX).
 *
 * Pattern used by Rightmove / SeLoger / Homegate to reduce footer
 * scroll length on small screens.
 */
const FooterGroup = ({ icon: Icon, title, tint = 'orange', testid, children, titleHref, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const tints = {
    orange: 'from-orange-500 to-red-500',
    amber: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-teal-500',
    blue: 'from-blue-500 to-indigo-500',
    purple: 'from-purple-500 to-fuchsia-500',
  };
  const titleNode = titleHref ? (
    <Link to={titleHref} className="hover:text-brand-400 transition-colors" data-testid={`${testid}-title-link`}>
      {title}
    </Link>
  ) : (
    title
  );
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 mb-3 sm:mb-5 sm:pointer-events-none sm:cursor-default"
        aria-expanded={open}
        aria-controls={`${testid}-content`}
        data-testid={`${testid}-toggle`}
      >
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br ${tints[tint] || tints.orange} shadow-md shadow-black/30`}>
          <Icon className="h-4 w-4 text-white" strokeWidth={2.2} />
        </div>
        <h3 className="flex-1 text-left text-white font-display font-bold text-sm uppercase tracking-wider">{titleNode}</h3>
        <ChevronDown
          className={`h-4 w-4 text-stone-400 transition-transform sm:hidden ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <div
        id={`${testid}-content`}
        className={`${open ? 'block' : 'hidden'} sm:block`}
      >
        {children}
      </div>
    </div>
  );
};

const Footer = () => {
  const { t, i18n } = useTranslation();
  const hasTutorials = (useTutorials() || []).length > 0;
  const aboutHtml = useCms('footer.about', "Realty237 est la plateforme de référence pour l'immobilier au Cameroun. Achat, vente, location et investissement.");
  const copyrightCms = useCms('footer.copyright', '© {YEAR} Realty237. Tous droits réservés.');
  // R8 — Always render the current year (auto-rollover on Jan 1st).
  // Supports `{YEAR}` placeholder OR a legacy 4-digit year in the CMS string.
  const currentYear = new Date().getFullYear();
  const copyright = copyrightCms.includes('{YEAR}')
    ? copyrightCms.replace('{YEAR}', currentYear)
    : copyrightCms.replace(/\b(19|20)\d{2}\b/, currentYear);
  const contactPhone = useCms('global.contact_phone', '+237 6 99 00 00 00');
  const contactEmail = useCms('global.contact_email', 'info@realty237.com');
  const facebookUrl = useCms('social.facebook_url', '');
  const twitterUrl = useCms('social.twitter_url', '');
  const instagramUrl = useCms('social.instagram_url', '');
  const linkedinUrl = useCms('social.linkedin_url', '');
  const youtubeUrl = useCms('social.youtube_url', '');
  const tiktokUrl = useCms('social.tiktok_url', '');
  const whatsappUrl = useCms('social.whatsapp_url', '');
  const telegramUrl = useCms('social.telegram_url', '');

  // Order priority: WhatsApp Business, Instagram, LinkedIn, Facebook first
  // (most-used channels in Cameroon). Extras (Twitter, YouTube, TikTok, Telegram)
  // appear afterwards when admin fills their URL.
  const socials = [
    { url: whatsappUrl, Icon: WhatsAppIcon, label: 'WhatsApp', testid: 'footer-social-whatsapp' },
    { url: instagramUrl, Icon: Instagram, label: 'Instagram', testid: 'footer-social-instagram' },
    { url: linkedinUrl, Icon: Linkedin, label: 'LinkedIn', testid: 'footer-social-linkedin' },
    { url: facebookUrl, Icon: Facebook, label: 'Facebook', testid: 'footer-social-facebook' },
    { url: twitterUrl, Icon: Twitter, label: 'Twitter', testid: 'footer-social-twitter' },
    { url: youtubeUrl, Icon: Youtube, label: 'YouTube', testid: 'footer-social-youtube' },
    { url: tiktokUrl, Icon: TikTokIcon, label: 'TikTok', testid: 'footer-social-tiktok' },
    { url: telegramUrl, Icon: TelegramIcon, label: 'Telegram', testid: 'footer-social-telegram' },
  ].filter((s) => s.url && s.url.trim() && s.url.trim() !== '#');
  return (
    <footer id="footer-section" className="bg-stone-900 text-stone-300 mt-20 pb-20 md:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <NewsletterSignup />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-x-8 gap-y-10">
          {/* About */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="bg-gradient-to-br from-brand-500 to-brand-600 p-2 rounded-xl shadow-md">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-white">Realty237</span>
            </div>
            <div className="text-sm mb-5 prose prose-invert prose-sm max-w-none [&_p]:m-0 [&_p+p]:mt-2 text-stone-400" dangerouslySetInnerHTML={{ __html: aboutHtml }} />
          </div>

          {/* Quick Links */}
          <FooterGroup icon={LinkIcon} title={t('footer.quickLinks')} testid="footer-section-quick" tint="blue">
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/properties?purpose=sale" className="text-stone-400 hover:text-brand-400 transition-colors">{t('nav.buy')}</Link></li>
              <li><Link to="/properties?purpose=rent" className="text-stone-400 hover:text-brand-400 transition-colors">{t('nav.rent')}</Link></li>
              <li><Link to="/properties?type=land" className="text-stone-400 hover:text-brand-400 transition-colors">{t('nav.land')}</Link></li>
              <li><Link to="/about" className="text-stone-400 hover:text-brand-400 transition-colors">{t('footer.about')}</Link></li>
              <li><Link to="/developers" className="text-stone-400 hover:text-brand-400 transition-colors inline-flex items-center gap-1.5" data-testid="footer-link-developers">
                Développeurs (API)
                <span className="text-label font-bold bg-orange-500 text-white px-1 py-0.5 rounded uppercase tracking-wider">B2B</span>
              </Link></li>
              <li><Link to="/terms" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-terms">{t('footer.terms')}</Link></li>
              <li><Link to="/legal/api-terms" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-api-terms">CGU Data API</Link></li>
              <li><Link to="/privacy" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-privacy">{t('footer.privacy')}</Link></li>
            </ul>
          </FooterGroup>

          {/* Services (Immobilier — particuliers & agents) */}
          <FooterGroup icon={Wrench} title={t('footer.services')} testid="footer-section-services" tint="orange">
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/forfaits" className="text-stone-400 hover:text-brand-400 transition-colors inline-flex items-center gap-1.5" data-testid="footer-link-all-plans">
                Tous les forfaits
                <BetaBadge label="Nouveau" variant="amber" testId="footer-badge-forfaits" />
              </Link></li>
              <li><Link to="/publier" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-publish">{t('footer.publishAd')}</Link></li>
              <li><Link to="/loan-calculator" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-loan">{t('footer.loanCalculator')}</Link></li>
              <li><Link to="/estimation" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-estimation">{t('footer.estimateProperty')}</Link></li>
              <li><Link to="/sell-with-us" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-sell">{t('footer.sellWithUs')}</Link></li>
              <li><Link to="/market-reports" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-reports">{t('footer.marketReports')}</Link></li>
              <li><a href="/#news" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-news">{t('footer.advice')}</a></li>
              {hasTutorials && (
                <li><Link to="/tutoriels" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-tutorials">Tutoriels vidéo</Link></li>
              )}
            </ul>
          </FooterGroup>

          {/* Banques & Assurances */}
          <FooterGroup icon={Landmark} title={t('footer.banksInsurance')} testid="footer-section-banks" tint="emerald">
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/partners" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-financial-partners">{t('footer.financialPartners')}</Link></li>
              <li><Link to="/devenir-partenaire" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-become-partner">Devenir partenaire</Link></li>
              <li>
                <Link to="/reprises-bancaires" className="text-stone-400 hover:text-brand-400 transition-colors inline-flex items-center gap-1.5" data-testid="footer-link-bank-repossession-banks">
                  {t('footer.bankRepossessions')}
                  <BetaBadge label={t('common.new')} variant="amber" testId="footer-badge-reprises" />
                </Link>
              </li>
              <li><Link to="/publier" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-publish-bank-repossession">{t('footer.publishBankRepossession')}</Link></li>
            </ul>
          </FooterGroup>

          {/* Partenaires Pro */}
          <FooterGroup icon={Briefcase} title={t('footer.proPartners')} testid="footer-section-pros" tint="amber">
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/legal-pros" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-legal-pros">{t('footer.legalPros')}</Link></li>
              <li><Link to="/services" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-services">{t('footer.servicesMarketplace')}</Link></li>
              <li><Link to="/become-pro" className="text-stone-400 hover:text-brand-400 transition-colors" data-testid="footer-link-become-pro">{t('footer.becomePro')}</Link></li>
              <li><Link to="/pro-dashboard" className="text-stone-400 hover:text-brand-400 transition-colors inline-flex items-center gap-1.5" data-testid="footer-link-pro-dashboard">
                {t('footer.proSpace')}
                <BetaBadge label={t('common.new')} variant="amber" testId="footer-badge-pro-space" />
              </Link></li>
            </ul>
          </FooterGroup>

          {/* Contact */}
          <FooterGroup icon={Mail} title={t('footer.contact')} testid="footer-section-contact" tint="purple" titleHref="/contact">
            <ul className="space-y-3 text-sm">
              <li className="flex items-center space-x-2 text-stone-400">
                <MapPin className="h-4 w-4 text-brand-500 shrink-0" />
                <span>{t('footer.address')}</span>
              </li>
              <li className="flex items-center space-x-2 text-stone-400">
                <Phone className="h-4 w-4 text-brand-500 shrink-0" />
                <span>{contactPhone}</span>
              </li>
              <li className="flex items-center space-x-2 text-stone-400">
                <Mail className="h-4 w-4 text-brand-500 shrink-0" />
                <span>{contactEmail}</span>
              </li>
            </ul>
          </FooterGroup>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-8">
          {socials.length > 0 && (
            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-6"
              data-testid="footer-socials-strip"
            >
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-stone-400">
                Suivez-nous
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2.5" data-testid="footer-socials">
                {socials.map(({ url, Icon, label, testid }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    data-testid={testid}
                    className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-stone-700 text-white ring-1 ring-stone-600 hover:bg-brand-600 hover:ring-brand-500 hover:scale-110 hover:shadow-lg hover:shadow-brand-600/40 transition-all duration-200"
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div
            className="flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-between gap-4 text-center sm:text-left"
            data-testid="footer-bottom-bar"
          >
            <div className="flex flex-col items-center sm:items-start gap-1.5">
              <p className="text-sm text-stone-500" data-testid="footer-copyright">{copyright}</p>
              {/* R8 — Data-freshness trust signal (pattern SeLoger / LeBonCoin). */}
              <p
                className="inline-flex items-center gap-1.5 text-label text-stone-500"
                data-testid="footer-data-freshness"
              >
                <RefreshCw className="h-3 w-3 text-emerald-400" aria-hidden="true" />
                <span>Données mises à jour quotidiennement</span>
              </p>
              {/* Emergent branding — moved from a floating badge to a
                  discreet inline text link so it stays out of the way
                  and doesn't hover over the mobile UI. */}
              <p
                className="text-label text-stone-500"
                data-testid="footer-made-with-emergent"
              >
                <a
                  href="https://app.emergent.sh/?utm_source=emergent-badge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-orange-400 transition-colors"
                >
                  Made with Emergent
                </a>
              </p>
            </div>

            <div
              className="inline-flex items-center gap-1 rounded-full bg-stone-800/80 ring-1 ring-stone-700 p-1"
              role="group"
              aria-label="Langue / Language"
              data-testid="footer-language-toggle"
              data-no-translate
            >
              <Languages className="h-3.5 w-3.5 text-stone-400 ml-2" aria-hidden="true" />
              {[['fr', 'Français'], ['en', 'English']].map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  aria-pressed={i18n.language === code}
                  onClick={() => setLanguage(code)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${i18n.language === code ? 'text-white bg-brand-600 shadow-sm' : 'text-stone-400 hover:text-white'}`}
                  data-testid={`footer-lang-${code}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
